import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import { Divider, Theme, useMediaQuery } from '@mui/material';

import useIsMounted from 'hooks/use-is-mounted';
import LicenseGuard from 'main/components/LicenseGuard';
import SearchBar from 'main/components/SearchBar';
import AuthWidget from 'pages/components/AuthWidget';
import { MenuContextProvider } from 'pages/layout/components/MenuContext';
import MenuItem from 'pages/layout/components/MenuItem';
import { getRouteInfoByLink, menuRoutes } from 'pages/layout/components/MenuList/routes';

import useStyles from './styles';

interface Props {
    adaptive?: boolean;
    showSearchBar?: boolean;
    className?: string;
    onMenuItemClick?: () => void;
}

const MenuList: React.FC<Props> = ({ className, showSearchBar, adaptive = false, onMenuItemClick }) => {
    const { classes, cx } = useStyles();
    const location = useLocation();
    const isMounted = useIsMounted();
    const isCompact = useMediaQuery<Theme>((theme) => theme.breakpoints.between(480, 1025)) && adaptive;

    const contextValue = useMemo(() => {
        const { active, expanded } = getRouteInfoByLink(location.pathname);
        return { adaptive, active, expanded };
    }, [adaptive, location.pathname]);

    const renderRoutes = useCallback(
        (routes: typeof menuRoutes, indent = 0) =>
            routes
                .filter(({ virtual }) => !virtual)
                .map(({ key, link, text, divider, icon, children = [], ...rest }) => (
                    <LicenseGuard
                        key={key}
                        enabled={key === 'assessment_perimeter_control'}
                        licenses={['trial', 'paid']}
                    >
                        {divider === 'top' || divider === 'both' ? <Divider className={classes.divider} /> : null}
                        <MenuItem
                            itemKey={key}
                            indent={indent}
                            link={link}
                            text={text}
                            icon={icon}
                            onClick={onMenuItemClick}
                            compact={isCompact}
                            {...rest}
                        >
                            {renderRoutes(children, indent + 1)}
                        </MenuItem>
                        {divider === 'bottom' || divider === 'both' ? <Divider className={classes.divider} /> : null}
                    </LicenseGuard>
                )),
        [classes.divider, isCompact, onMenuItemClick]
    );

    return (
        <MenuContextProvider value={contextValue}>
            <div className={cx(classes.container, className)}>
                {showSearchBar && <SearchBar compact className={classes.searchBarRoot} />}
                <div className={classes.wrapper}>
                    <div className={cx(classes.menuList, { [classes.menuListAdaptive]: adaptive })}>
                        {renderRoutes(menuRoutes)}
                    </div>
                </div>
                <AuthWidget compact={isCompact} hidden={!isMounted} />
            </div>
        </MenuContextProvider>
    );
};

export default MenuList;
